import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import MyLocationRounded from '@mui/icons-material/MyLocationRounded';
import { Autocomplete, GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useForm } from 'react-hook-form';

import Button from './Button';
import Modal from './Modal';
import CheckoutSectionHeading from './checkout/CheckoutSectionHeading';
import CheckoutSelectionCard from './checkout/CheckoutSelectionCard';
import { v2Colors } from '../design/theme';
import { getCurrentPosition, requestLocationPermission } from '../platform/geolocation';
import { addPilotUserAddress, getPilotUserAddresses } from '../services/addresses';
import { V2ApiError } from '../services/client';
import { readLocationCache } from '../services/locationCache';
import {
  deliveryUnavailableMessage,
  formatAddressLine,
  resolveAndMergeForAddress,
} from '../services/locationResolve';
import { useV2Session } from '../state/SessionProvider';

const MAP_LIBRARIES = ['places'];
const DEFAULT_CENTER = { lat: 17.38714, lng: 78.491684 };
const ADDRESS_PREVIEW_COUNT = 3;

const searchInputStyle = {
  boxSizing: 'border-box',
  border: '1px solid #ccc',
  width: '100%',
  maxWidth: '100%',
  height: 40,
  padding: '0 12px',
  borderRadius: 4,
  fontSize: 14,
  marginBottom: 12,
  outline: 'none',
};

function AddressFormFields({ register, errors }) {
  return (
    <>
      <TextField
        label="Full name"
        fullWidth
        {...register('userName', { required: 'Name is required' })}
        error={Boolean(errors.userName)}
        helperText={errors.userName?.message}
      />
      <TextField
        label="House / flat / door"
        fullWidth
        {...register('houseDetails', { required: 'Required' })}
        error={Boolean(errors.houseDetails)}
        helperText={errors.houseDetails?.message}
      />
      <TextField label="Street / area" fullWidth {...register('streetDetails')} />
      <TextField label="Landmark" fullWidth {...register('landmark')} />
      <TextField
        label="Pincode"
        fullWidth
        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
        {...register('pincode', {
          required: 'Pincode required',
          pattern: { value: /^\d{6}$/, message: '6-digit pincode' },
        })}
        error={Boolean(errors.pincode)}
        helperText={errors.pincode?.message}
      />
    </>
  );
}

/** Google map + Places — only mount when `mapKey` is non-empty (valid hook key). */
function AddAddressFormMap({ mapKey, onSave, onCancel }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { userName: '', houseDetails: '', streetDetails: '', landmark: '', pincode: '' } });

  const [latLong, setLatLong] = useState(DEFAULT_CENTER);
  const [searchResult, setSearchResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [locating, setLocating] = useState(false);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-v2-checkout-address',
    googleMapsApiKey: mapKey,
    libraries: MAP_LIBRARIES,
  });

  const onMapLoad = useCallback((mapInstance) => {
    mapRef.current = mapInstance;
    mapInstance.setCenter(DEFAULT_CENTER);
    mapInstance.setZoom(17);
  }, []);

  const useCurrentLocation = useCallback(async () => {
    setFormError('');
    setLocating(true);
    try {
      const perm = await requestLocationPermission();
      if (perm !== 'granted') {
        setFormError('Location permission is required. Enable it in settings or pin the map manually.');
        return;
      }
      const pos = await getCurrentPosition();
      if (!pos) {
        setFormError('Could not read your location. Try again or move the pin on the map.');
        return;
      }
      setLatLong(pos);
      mapRef.current?.panTo(pos);
      mapRef.current?.setZoom(17);
    } finally {
      setLocating(false);
    }
  }, []);
  const onMarkerDragEnd = useCallback((e) => {
    if (!e?.latLng) return;
    setLatLong({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, []);

  const onAutoCompleteLoad = useCallback((ac) => {
    setSearchResult(ac);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (!searchResult) return;
    const place = searchResult.getPlace();
    const loc = place?.geometry?.location;
    if (!loc) {
      setFormError('Pick a place from the suggestions list.');
      return;
    }
    setLatLong({ lat: loc.lat(), lng: loc.lng() });
    setFormError('');
  }, [searchResult]);

  const onSubmit = async (data) => {
    setFormError('');
    if (!isLoaded) {
      setFormError('Map is still loading. Please wait.');
      return;
    }
    if (!Number.isFinite(latLong.lat) || !Number.isFinite(latLong.lng)) {
      setFormError('Set a location on the map or search for a place.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        userName: String(data.userName).replace(/'/g, ''),
        houseDetails: String(data.houseDetails).replace(/'/g, ''),
        streetDetails: String(data.streetDetails || '').replace(/'/g, ''),
        landmark: String(data.landmark || '').replace(/'/g, ''),
        pincode: String(data.pincode).trim(),
        latLong,
      };
      await onSave(body);
    } catch (e) {
      setFormError(e instanceof V2ApiError ? e.message : 'Could not save address.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, maxWidth: '100%' }}
    >
      {formError ? <Alert severity="error">{formError}</Alert> : null}

      {!isLoaded ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={locating ? <CircularProgress size={14} color="inherit" /> : <MyLocationRounded />}
            disabled={!isLoaded || locating}
            onClick={useCurrentLocation}
            sx={{ borderRadius: '5px', alignSelf: 'stretch' }}
          >
            {locating ? 'Locating…' : 'Use current location'}
          </Button>
          <div style={{ width: '100%' }}>
            <Autocomplete onLoad={onAutoCompleteLoad} onPlaceChanged={onPlaceChanged}>
              <input type="text" placeholder="Search for your area" style={searchInputStyle} />
            </Autocomplete>
          </div>
          <Box sx={{ height: 220, width: '100%', maxWidth: '100%', borderRadius: 1, overflow: 'hidden' }}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={latLong}
              zoom={17}
              onLoad={onMapLoad}
              options={{
                mapTypeControl: false,
                fullscreenControl: false,
                streetViewControl: false,
                rotateControl: false,
                minZoom: 8,
              }}
            >
              <Marker position={latLong} draggable onDragEnd={onMarkerDragEnd} />
            </GoogleMap>
          </Box>
        </>
      )}

      <AddressFormFields register={register} errors={errors} />

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button type="button" variant="outlined" fullWidth onClick={onCancel} sx={{ borderRadius: '5px' }}>
          Cancel
        </Button>
        <Button type="submit" fullWidth disabled={submitting || !isLoaded} sx={{ borderRadius: '5px' }}>
          {submitting ? 'Saving…' : 'Save address'}
        </Button>
      </Box>
    </Box>
  );
}

/** Fallback when REACT_APP_MAP_KEY is unset — manual coordinates. */
function AddAddressFormManual({ onSave, onCancel }) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userName: '',
      houseDetails: '',
      streetDetails: '',
      landmark: '',
      pincode: '',
      lat: String(DEFAULT_CENTER.lat),
      lng: String(DEFAULT_CENTER.lng),
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [locating, setLocating] = useState(false);

  const useCurrentLocation = useCallback(async () => {
    setFormError('');
    setLocating(true);
    try {
      const perm = await requestLocationPermission();
      if (perm !== 'granted') {
        setFormError('Location permission is required.');
        return;
      }
      const pos = await getCurrentPosition();
      if (!pos) {
        setFormError('Could not read your location.');
        return;
      }
      setValue('lat', String(pos.lat), { shouldValidate: true });
      setValue('lng', String(pos.lng), { shouldValidate: true });
    } finally {
      setLocating(false);
    }
  }, [setValue]);

  const onSubmit = async (data) => {
    setFormError('');
    const lat = Number(data.lat);
    const lng = Number(data.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setFormError('Enter valid latitude and longitude.');
      return;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setFormError('Coordinates out of range.');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        userName: String(data.userName).replace(/'/g, ''),
        houseDetails: String(data.houseDetails).replace(/'/g, ''),
        streetDetails: String(data.streetDetails || '').replace(/'/g, ''),
        landmark: String(data.landmark || '').replace(/'/g, ''),
        pincode: String(data.pincode).trim(),
        latLong: { lat, lng },
      };
      await onSave(body);
    } catch (e) {
      setFormError(e instanceof V2ApiError ? e.message : 'Could not save address.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {formError ? <Alert severity="error">{formError}</Alert> : null}
      <Alert severity="info">
        Set latitude and longitude for the delivery pin (e.g. from Google Maps). Add REACT_APP_MAP_KEY for map search.
      </Alert>
      <Button
        type="button"
        variant="outlined"
        size="small"
        startIcon={locating ? <CircularProgress size={14} color="inherit" /> : <MyLocationRounded />}
        disabled={locating}
        onClick={useCurrentLocation}
        sx={{ borderRadius: '5px', alignSelf: 'flex-start' }}
      >
        {locating ? 'Locating…' : 'Use current location'}
      </Button>
      <TextField
        label="Latitude"
        fullWidth
        {...register('lat', { required: 'Required' })}
        error={Boolean(errors.lat)}
        helperText={errors.lat?.message}
      />
      <TextField
        label="Longitude"
        fullWidth
        {...register('lng', { required: 'Required' })}
        error={Boolean(errors.lng)}
        helperText={errors.lng?.message}
      />
      <AddressFormFields register={register} errors={errors} />
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button type="button" variant="outlined" fullWidth onClick={onCancel} sx={{ borderRadius: '5px' }}>
          Cancel
        </Button>
        <Button type="submit" fullWidth disabled={submitting} sx={{ borderRadius: '5px' }}>
          {submitting ? 'Saving…' : 'Save address'}
        </Button>
      </Box>
    </Box>
  );
}

export default function CheckoutAddresses({
  onAfterLocationMerge,
  heading = 'Delivery address',
  variant = 'default',
}) {
  const { mobile } = useV2Session();
  const isCheckout = variant === 'checkout';
  const mapKey = (process.env.REACT_APP_MAP_KEY || '').trim();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectError, setSelectError] = useState('');
  const [resolvingStore, setResolvingStore] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [showAllAddresses, setShowAllAddresses] = useState(false);

  const loadList = useCallback(async () => {
    if (!mobile) return;
    setLoading(true);
    setListError('');
    try {
      const list = await getPilotUserAddresses(mobile);
      setAddresses(list);
      setShowAllAddresses(false);
    } catch (e) {
      setListError(e instanceof V2ApiError ? e.message : 'Could not load addresses.');
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, [mobile]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loc = await readLocationCache();
      const id = loc?.customerAddress?.addressId;
      if (!cancelled && id) setSelectedId(String(id));
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelectAddress = useCallback(async (addr) => {
    if (!addr?.addressId) return;
    setSelectedId(String(addr.addressId));
    setSelectError('');
    const lat = Number(addr.latLong?.lat);
    const lng = Number(addr.latLong?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setSelectError('This address has no map coordinates. Add a new address with a map pin or coordinates.');
      return;
    }

    setResolvingStore(true);
    let resolvedStoreId = null;
    try {
      const { storeId } = await resolveAndMergeForAddress(addr);
      resolvedStoreId = storeId;
    } catch (e) {
      setSelectError(deliveryUnavailableMessage(e));
      resolvedStoreId = null;
    } finally {
      setResolvingStore(false);
      onAfterLocationMerge?.({ storeId: resolvedStoreId });
    }
  }, [onAfterLocationMerge]);

  const handleAddSuccess = useCallback(
    async (body) => {
      const address = await addPilotUserAddress(mobile, body);
      await loadList();
      setAddOpen(false);
      if (address) {
        await handleSelectAddress(address);
      }
      return address;
    },
    [mobile, loadList, handleSelectAddress]
  );

  const orderedAddresses = useMemo(() => {
    if (!selectedId) return addresses;
    const sel = addresses.find((a) => String(a.addressId) === String(selectedId));
    if (!sel) return addresses;
    const rest = addresses.filter((a) => String(a.addressId) !== String(selectedId));
    return [sel, ...rest];
  }, [addresses, selectedId]);

  const addressListExpanded = useMemo(() => {
    if (orderedAddresses.length <= ADDRESS_PREVIEW_COUNT) return true;
    return showAllAddresses;
  }, [orderedAddresses.length, showAllAddresses]);

  const visibleAddresses = useMemo(() => {
    if (addressListExpanded) return orderedAddresses;
    return orderedAddresses.slice(0, ADDRESS_PREVIEW_COUNT);
  }, [orderedAddresses, addressListExpanded]);

  const showAddressShowMore =
    orderedAddresses.length > ADDRESS_PREVIEW_COUNT && !addressListExpanded;
  const showAddressShowLess =
    orderedAddresses.length > ADDRESS_PREVIEW_COUNT && showAllAddresses;

  if (!mobile) {
    return <Alert severity="info">Sign in to manage delivery addresses.</Alert>;
  }

  const headingEl = isCheckout ? (
    <CheckoutSectionHeading>{heading}</CheckoutSectionHeading>
  ) : (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
      {heading}
    </Typography>
  );

  return (
    <Box>
      {headingEl}

      {listError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {listError}
        </Alert>
      ) : null}
      {selectError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {selectError}
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={32} />
        </Box>
      ) : addresses.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No saved addresses yet.
        </Typography>
      ) : (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            opacity: resolvingStore ? 0.7 : 1,
            pointerEvents: resolvingStore ? 'none' : 'auto',
          }}
        >
          {visibleAddresses.map((addr) => {
            const id = String(addr.addressId);
            const selected = selectedId === id;
            return (
              <CheckoutSelectionCard
                key={id}
                selected={selected}
                onClick={() => handleSelectAddress(addr)}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25 }}>
                  {addr.userName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                  {formatAddressLine(addr)}
                </Typography>
                {isCheckout && mobile ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {mobile}
                  </Typography>
                ) : null}
              </CheckoutSelectionCard>
            );
          })}
          {showAddressShowMore ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setShowAllAddresses(true)}
                sx={{
                  border: 'none',
                  margin: 0,
                  cursor: 'pointer',
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  typography: 'body2',
                  fontWeight: 600,
                  color: v2Colors.primary,
                  fontFamily: 'inherit',
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                  '&:hover': { opacity: 0.85 },
                }}
              >
                Show all addresses
              </Box>
            </Box>
          ) : null}
          {showAddressShowLess ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="button"
                type="button"
                onClick={() => setShowAllAddresses(false)}
                sx={{
                  border: 'none',
                  margin: 0,
                  cursor: 'pointer',
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  typography: 'body2',
                  color: v2Colors.primaryMuted,
                  fontFamily: 'inherit',
                  bgcolor: 'transparent',
                  '&:hover': { opacity: 0.85 },
                }}
              >
                Show less
              </Box>
            </Box>
          ) : null}
        </Box>
      )}

      {resolvingStore ? (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Finding nearest store…
          </Typography>
        </Box>
      ) : null}

      {isCheckout ? (
        <Box
          component="button"
          type="button"
          onClick={() => setAddOpen(true)}
          sx={{
            width: '100%',
            py: 1.5,
            borderRadius: 2,
            border: `1px dashed ${v2Colors.primary}`,
            bgcolor: v2Colors.canvas,
            color: v2Colors.primary,
            fontWeight: 600,
            fontSize: '0.9rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
            '&:hover': { bgcolor: v2Colors.selectedFill },
          }}
        >
          + Add new address
        </Box>
      ) : (
        <Button variant="outlined" fullWidth onClick={() => setAddOpen(true)} sx={{ borderRadius: '5px' }}>
          Add new address
        </Button>
      )}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add address"
        fullScreen
      >
        {mapKey ? (
          <AddAddressFormMap mapKey={mapKey} onSave={handleAddSuccess} onCancel={() => setAddOpen(false)} />
        ) : (
          <AddAddressFormManual onSave={handleAddSuccess} onCancel={() => setAddOpen(false)} />
        )}
      </Modal>
    </Box>
  );
}
